/**
 * TimeInput - A touch-friendly time input component
 * 
 * Features:
 * - Type time directly (e.g., "930a", "9:30 AM", "14:30")
 * - Click to show dropdown with common times
 * - Large touch targets for mobile/tablet
 * - Auto-formats input as user types
 */

import React, { useState, useRef, useEffect } from 'react';
import { Input } from './input';
import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Clock, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';

// Generate time options in 15-minute intervals
const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const h24 = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      const h12 = formatTo12Hour(h24);
      options.push({ value: h24, label: h12 });
    }
  }
  return options;
};

// Convert 24-hour time to 12-hour format
const formatTo12Hour = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${String(minutes).padStart(2, '0')} ${period}`;
};

// Apply smart AM/PM assumption for a business-day context when the user
// didn't specify a period. Rule (from user spec):
//   hours 9–11 → AM (as typed)
//   hours 12   → 12 PM (noon, as typed)
//   hours 1–8  → PM (add 12)
//   hours 0 or 13–23 → left as-is (already 24-hour)
const applySmartAmPm = (h) => {
  if (h >= 1 && h <= 8) return h + 12;
  return h;
};

// Parse various time formats to 24-hour format
const parseTimeInput = (input) => {
  if (!input) return null;
  
  // Remove extra spaces and lowercase
  const cleaned = input.trim().toLowerCase().replace(/\s+/g, ' ');
  
  // Already in 24-hour format (HH:MM) — apply smart AM/PM for ambiguous 1-8 range
  if (/^\d{1,2}:\d{2}$/.test(cleaned)) {
    const [rawH, m] = cleaned.split(':').map(Number);
    const h = applySmartAmPm(rawH);
    if (h >= 0 && h < 24 && m >= 0 && m < 60) {
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
  }
  
  // Format: "9:30 AM" or "9:30AM" or "9:30 pm"
  const match12 = cleaned.match(/^(\d{1,2}):(\d{2})\s*(am|pm|a|p)?$/i);
  if (match12) {
    let [, h, m, period] = match12;
    h = parseInt(h, 10);
    m = parseInt(m, 10);
    
    if (period) {
      period = period.toLowerCase();
      if ((period === 'pm' || period === 'p') && h < 12) h += 12;
      if ((period === 'am' || period === 'a') && h === 12) h = 0;
    }
    
    if (h >= 0 && h < 24 && m >= 0 && m < 60) {
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
  }
  
  // Format: "930a" or "930p" or "0930" or "1430"
  const matchCompact = cleaned.match(/^(\d{3,4})\s*(am|pm|a|p)?$/i);
  if (matchCompact) {
    let [, digits, period] = matchCompact;
    let h, m;
    
    if (digits.length === 3) {
      h = parseInt(digits[0], 10);
      m = parseInt(digits.slice(1), 10);
    } else {
      h = parseInt(digits.slice(0, 2), 10);
      m = parseInt(digits.slice(2), 10);
    }
    
    if (period) {
      period = period.toLowerCase();
      if ((period === 'pm' || period === 'p') && h < 12) h += 12;
      if ((period === 'am' || period === 'a') && h === 12) h = 0;
    } else if (digits.length === 3) {
      // Ambiguous 3-digit compact like "130" — apply smart AM/PM (1-8 → PM).
      // 4-digit like "0130" is treated as explicit 24-hour notation.
      h = applySmartAmPm(h);
    }
    
    if (h >= 0 && h < 24 && m >= 0 && m < 60) {
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
  }
  
  // Format: bare hour "9" or "12" — apply smart AM/PM
  // (This must come AFTER the compact 3-4 digit match to avoid clashing.)
  const matchBareHour = cleaned.match(/^(\d{1,2})$/);
  if (matchBareHour) {
    let h = parseInt(matchBareHour[1], 10);
    h = applySmartAmPm(h);
    if (h >= 0 && h < 24) {
      return `${String(h).padStart(2, '0')}:00`;
    }
  }
  
  // Format: "9a" or "9p" or "9 am" (just hour)
  const matchHourOnly = cleaned.match(/^(\d{1,2})\s*(am|pm|a|p)$/i);
  if (matchHourOnly) {
    let [, h, period] = matchHourOnly;
    h = parseInt(h, 10);
    
    period = period.toLowerCase();
    if ((period === 'pm' || period === 'p') && h < 12) h += 12;
    if ((period === 'am' || period === 'a') && h === 12) h = 0;
    
    if (h >= 0 && h < 24) {
      return `${String(h).padStart(2, '0')}:00`;
    }
  }
  
  return null;
};

const TIME_OPTIONS = generateTimeOptions();

export function TimeInput({ 
  value, 
  onChange, 
  placeholder = "Type or select time...",
  className,
  disabled = false,
  id,
  "data-testid": dataTestId
}) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [filterQuery, setFilterQuery] = useState('');
  const inputRef = useRef(null);
  const listRef = useRef(null);
  
  // Sync input value with external value
  useEffect(() => {
    if (value) {
      setInputValue(formatTo12Hour(value));
    } else {
      setInputValue('');
    }
  }, [value]);
  
  // Handle typing in the input
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setFilterQuery(newValue);
    
    // Try to parse the input
    const parsed = parseTimeInput(newValue);
    if (parsed) {
      onChange?.(parsed);
    }
  };
  
  // Handle blur - format the time properly
  const handleBlur = () => {
    const parsed = parseTimeInput(inputValue);
    if (parsed) {
      setInputValue(formatTo12Hour(parsed));
      onChange?.(parsed);
    } else if (inputValue && !parsed) {
      // Invalid input - reset to previous valid value
      setInputValue(value ? formatTo12Hour(value) : '');
    }
  };
  
  // Handle selecting from dropdown
  const handleSelect = (time24) => {
    onChange?.(time24);
    setInputValue(formatTo12Hour(time24));
    setOpen(false);
    setFilterQuery('');
  };
  
  // Filter options based on input
  const filteredOptions = filterQuery
    ? TIME_OPTIONS.filter(opt => 
        opt.label.toLowerCase().includes(filterQuery.toLowerCase()) ||
        opt.value.includes(filterQuery)
      )
    : TIME_OPTIONS;
  
  // Scroll to current time or default to 8:00 AM when opening
  useEffect(() => {
    if (open && listRef.current) {
      const scrollToTarget = () => {
        if (!listRef.current) return;
        const targetValue = value || '08:00';
        const index = TIME_OPTIONS.findIndex(opt => opt.value === targetValue);
        if (index >= 0) {
          const itemHeight = 44;
          listRef.current.scrollTop = Math.max(0, (index - 2) * itemHeight);
        }
      };
      // Immediate try + delayed retry for popover render timing
      scrollToTarget();
      const timer = setTimeout(scrollToTarget, 50);
      return () => clearTimeout(timer);
    }
  }, [open, value]);
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className={cn("relative", className)}>
          <Input
            ref={inputRef}
            id={id}
            data-testid={dataTestId}
            type="text"
            inputMode="text"
            autoComplete="off"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            disabled={disabled}
            className="pr-10 h-9 text-base" // text-base for better touch readability
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-0 top-0 h-full px-2 hover:bg-transparent"
            onClick={() => setOpen(!open)}
            disabled={disabled}
            tabIndex={-1}
          >
            <ChevronDown className="h-4 w-4 text-gray-400" />
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[200px] p-0" 
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()} // Keep focus on input
      >
        <div 
          ref={listRef}
          className="max-h-[300px] overflow-y-auto"
        >
          {filteredOptions.length === 0 ? (
            <div className="p-3 text-sm text-gray-500 text-center">
              No matching times
            </div>
          ) : (
            filteredOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={cn(
                  "w-full px-4 py-3 text-left text-base hover:bg-gray-100 focus:bg-gray-100 focus:outline-none",
                  "min-h-[44px]", // Minimum touch target size
                  value === option.value && "bg-blue-50 text-blue-700 font-medium"
                )}
                onClick={() => handleSelect(option.value)}
              >
                {option.label}
              </button>
            ))
          )}
        </div>
        {/* Quick select common times */}
        <div className="border-t p-2 bg-gray-50">
          <p className="text-xs text-gray-500 mb-2">Quick select:</p>
          <div className="flex flex-wrap gap-1">
            {['09:00', '10:00', '11:00', '13:00', '14:00', '15:00'].map(time => (
              <Button
                key={time}
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => handleSelect(time)}
              >
                {formatTo12Hour(time)}
              </Button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default TimeInput;
